import { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import api, { downloadFile } from '../../utils/api';
import { FileText, Download, Calendar, Search, Filter, ChevronRight, Loader2, X } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import BackButton from '../../components/BackButton';

// Color palette for years
const YEAR_COLORS = {
  2026: { gradient: 'from-blue-500 to-indigo-600', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-500', light: 'bg-blue-100' },
  2025: { gradient: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-500', light: 'bg-emerald-100' },
  2024: { gradient: 'from-purple-500 to-violet-600', bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-500', light: 'bg-purple-100' },
  2023: { gradient: 'from-orange-500 to-amber-600', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', badge: 'bg-orange-500', light: 'bg-orange-100' },
  2022: { gradient: 'from-rose-500 to-pink-600', bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', badge: 'bg-rose-500', light: 'bg-rose-100' },
  default: { gradient: 'from-slate-500 to-gray-600', bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', badge: 'bg-slate-500', light: 'bg-slate-100' }
};

const getYearColor = (dateStr) => {
  const year = new Date(dateStr).getFullYear();
  return YEAR_COLORS[year] || YEAR_COLORS.default;
};

const Minutes = () => {
  const [minutes, setMinutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  
  // Search and filter
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');

  useEffect(() => { loadMinutes(); }, []);

  const loadMinutes = async () => {
    try {
      const response = await api.get('/minutes');
      setMinutes(response.data);
    } catch (error) {
      toast.error('Failed to load minutes');
    } finally {
      setLoading(false);
    }
  };

  // Get unique years for filter
  const years = useMemo(() => {
    const uniqueYears = [...new Set(minutes.map(m => new Date(m.date).getFullYear()))];
    return uniqueYears.sort((a, b) => b - a);
  }, [minutes]);

  // Filtered minutes
  const filteredMinutes = useMemo(() => {
    return minutes.filter(m => {
      const matchesSearch = m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           (m.content && m.content.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesYear = selectedYear === 'all' || new Date(m.date).getFullYear().toString() === selectedYear;
      return matchesSearch && matchesYear;
    });
  }, [minutes, searchQuery, selectedYear]);

  const handleDownload = async (fileUrl, title) => {
    try {
      await downloadFile(fileUrl, title);
      toast.success('Download started');
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="member-minutes-page">
      <BackButton to="/member" />
      
      {/* Header */}
      <div className="flex items-center space-x-4">
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-4 rounded-2xl shadow-lg">
          <FileText className="w-8 h-8 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-serif font-bold text-slate-900">Meeting Minutes</h1>
          <p className="text-slate-600">View and download official meeting records</p>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search minutes..."
            className="pl-10 border-slate-200"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Filter className="w-5 h-5 text-slate-400" />
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="bg-white border border-slate-200 rounded-lg px-4 py-2 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Years</option>
            {years.map(year => (
              <option key={year} value={year.toString()}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Year Color Legend */}
      {years.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs text-slate-500">Filter by year:</span>
          {years.slice(0, 5).map(year => {
            const colors = YEAR_COLORS[year] || YEAR_COLORS.default;
            return (
              <button
                key={year}
                onClick={() => setSelectedYear(selectedYear === year.toString() ? 'all' : year.toString())}
                className={`${colors.badge} text-white text-xs px-3 py-1 rounded-full font-medium transition-all ${
                  selectedYear === year.toString() ? 'ring-2 ring-offset-2 ring-blue-500' : 'opacity-80 hover:opacity-100'
                }`}
              >
                {year}
              </button>
            );
          })}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
          <p className="text-blue-600 text-sm font-medium">Total Minutes</p>
          <p className="text-2xl font-bold text-slate-900">{minutes.length}</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4">
          <p className="text-emerald-600 text-sm font-medium">This Year</p>
          <p className="text-2xl font-bold text-slate-900">{minutes.filter(m => new Date(m.date).getFullYear() === new Date().getFullYear()).length}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200 rounded-xl p-4">
          <p className="text-purple-600 text-sm font-medium">Showing</p>
          <p className="text-2xl font-bold text-slate-900">{filteredMinutes.length}</p>
        </div>
      </div>

      {/* Minutes Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : filteredMinutes.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredMinutes.map((minute) => {
            const colors = getYearColor(minute.date);
            const isExpanded = expandedId === minute.id;
            return (
              <div 
                key={minute.id} 
                className={`group relative bg-white rounded-2xl overflow-hidden border-2 ${colors.border} hover:shadow-xl transition-all duration-300`}
              >
                {/* Color stripe */}
                <div className={`h-2 bg-gradient-to-r ${colors.gradient}`} />
                
                <div className="p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className={`${colors.light} p-3 rounded-xl`}>
                      <FileText className={`w-6 h-6 ${colors.text}`} />
                    </div>
                    {minute.file_url && (
                      <Button
                        onClick={() => handleDownload(minute.file_url, minute.title)}
                        size="sm"
                        className={`bg-gradient-to-r ${colors.gradient} text-white shadow-md hover:shadow-lg transition-shadow`}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    )}
                  </div>
                  
                  {/* Year Badge */}
                  <span className={`inline-block ${colors.badge} text-white text-xs px-2 py-1 rounded-full font-medium mb-3`}>
                    {new Date(minute.date).getFullYear()}
                  </span>
                  
                  {/* Title */}
                  <h3 className={`font-semibold text-lg text-slate-900 mb-2 line-clamp-2 group-hover:${colors.text} transition-colors`}>
                    {minute.title}
                  </h3>
                  
                  {/* Summary */}
                  {minute.content && (
                    <p className={`text-sm text-slate-600 mb-4 ${isExpanded ? '' : 'line-clamp-3'}`}>
                      {minute.content}
                    </p>
                  )}
                  
                  {/* Footer */}
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div className="flex items-center text-xs text-slate-500 space-x-1">
                      <Calendar className="w-3 h-3" />
                      <span>{formatDate(minute.date)}</span>
                    </div>
                    {minute.content && minute.content.length > 150 && (
                      <button 
                        onClick={() => setExpandedId(isExpanded ? null : minute.id)}
                        className={`text-xs ${colors.text} hover:underline flex items-center`}
                      >
                        {isExpanded ? 'Show less' : 'Read more'}
                        <ChevronRight className={`w-3 h-3 ml-1 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl text-center py-16">
          <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-600 text-lg">No minutes found</p>
          <p className="text-sm text-slate-500 mt-2">
            {searchQuery || selectedYear !== 'all' ? 'Try adjusting your filters' : 'Check back later for meeting records'}
          </p>
        </div>
      )}
    </div>
  );
};

export default Minutes;
